import { api } from './api'
import { invoiceBreakdown, type BreakdownLine, type BreakdownVisit } from './invoice-breakdown'
import { extrasOf } from './extras'
import { getMe } from './me'
import { currentTrade } from './trade'
import type { Clean } from './types'
import { el, money, toast } from './ui'

type PaymentVisit = Clean & { breakdown_json?: string | null; live_invoice_id?: number | null; on_invoice?: string | null; customer_commercial?: number | null }

// The total still belongs to the visit. Ignore presentation made stale by a later ordinary price edit.
export function paymentBreakdownPrefix(visit: PaymentVisit): string {
  let lines: BreakdownLine[]
  try {
    lines = JSON.parse(visit.breakdown_json ?? 'null')
    if (!Array.isArray(lines) || !lines.length || lines.length > 12 || lines.some(l => !l || typeof l.name !== 'string' || !l.name.trim() || l.name.length > 60 || /[\u0000-\u001f\u007f\u2014]/.test(l.name) || !Number.isInteger(l.count) || l.count < 1 || l.count > 99 || !Number.isSafeInteger(l.each_pence) || l.each_pence < 0 || l.each_pence > 1_000_000)) return ''
  } catch { return '' }
  const fixed: BreakdownLine[] = [
    ...(Number(visit.inside_pence) > 0 ? [{ name: currentTrade() === 'window_cleaning' ? 'Insides' : 'Inside work', count: 1, each_pence: Number(visit.inside_pence) }] : []),
    ...extrasOf(visit.extras_json).filter(e => Number.isInteger(e.each_pence)).map(e => ({ name: e.name, count: e.count, each_pence: e.each_pence! })),
  ]
  const all = [...lines, ...fixed]
  if (all.reduce((s, l) => s + l.count * l.each_pence, 0) !== visit.amount_pence) return ''
  return all.map(l => `${l.count > 1 ? `${l.count} ${l.name.charAt(0).toLowerCase()}${l.name.slice(1)} at ${money(l.each_pence)}` : l.name}: ${money(l.count * l.each_pence)}`).join('\n') + `\nTotal: ${money(visit.amount_pence)}\n\n`
}

export const paymentBreakdownMessage = (visit: PaymentVisit, words: string): string => paymentBreakdownPrefix(visit) + words

interface Preview { clean: PaymentVisit; owed_pence: number; visit: BreakdownVisit; prices: Parameters<typeof invoiceBreakdown>[1] }

export function owedBreakdown(visit: PaymentVisit, opts: {
  onVisitSaved?: (saved: { id: number; version: number }) => void
  onSaved: (clean: PaymentVisit, owed: number) => void
}) {
  const node = el('div', { class: 'owed-payment-breakdown' })
  let editor: ReturnType<typeof invoiceBreakdown> | null = null, loading = false, refreshing = false
  const permitted = getMe()?.role === 'owner' && visit.state === 'done' && visit.carryover !== 1 && visit.customer_commercial !== 1 && !visit.live_invoice_id && !visit.on_invoice
  const open = el('button', { type: 'button', class: 'link-button', 'aria-expanded': 'false' }, 'Show the breakdown')
  const error = el('p', { class: 'form-error', role: 'alert' })
  const preview = (): Promise<Preview> => api<Preview>(`/api/cleans/${visit.id}/breakdown`)
  const reload = async (): Promise<void> => {
    refreshing = true
    try {
      const fresh = await preview()
      if (!node.isConnected) return
      opts.onSaved({ ...visit, ...fresh.clean }, fresh.owed_pence)
    } catch (e) {
      if (!node.isConnected) return
      error.textContent = `The breakdown saved. Check your signal and try loading the payment message again. ${(e as Error).message}`
      const retry = el('button', { type: 'button', class: 'btn-secondary' }, 'Reload payment message')
      retry.addEventListener('click', () => { retry.remove(); void reload() }); node.append(retry)
      return
    }
    refreshing = false
  }
  open.addEventListener('click', () => {
    if (loading) return
    loading = true; open.disabled = true
    void preview().then(fresh => {
      if (!node.isConnected) return
      editor = invoiceBreakdown([fresh.visit], fresh.prices, { onPreview() {}, onSaved: () => void reload(), onVisitSaved: opts.onVisitSaved,
        changedWords: ' Close this payment message and open it again to check the saved amounts.',
        onLocked: (e) => { refreshing = true; error.textContent = (e as Error).message } })
      node.replaceChildren(editor.node, error)
      editor.node.querySelector<HTMLButtonElement>('button')?.click()
    }).catch(e => { error.textContent = (e as Error).message }).finally(() => { loading = false; open.disabled = false })
  })
  if (permitted) node.append(open, error)
  return { node, guard: (e: Event): void => {
    if (loading || refreshing || editor?.dirty() || editor?.busy()) { e.preventDefault(); e.stopImmediatePropagation(); toast('Save the breakdown and load the payment message before sending.') }
  } }
}
